/**
 * Accessible modal dialog (QA56): role=dialog + aria-modal, labelled by its title, focus is
 * moved inside and trapped (Tab / Shift+Tab cycle), Esc closes, focus returns to the opener.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const dialogStack: HTMLDivElement[] = [];
let bodyOverflow = '';

const topDialog = () => dialogStack[dialogStack.length - 1];
const focusableItems = (el: HTMLElement) => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((item) => item.tabIndex >= 0 && item.getClientRects().length > 0);

function updateModalLayers() {
  for (const dialog of dialogStack) dialog.inert = dialog !== topDialog();
}

export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  dismissible = true,
  className,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
  /** When false, Esc/backdrop do nothing (a decision is required, e.g. the conflict dialog). */
  dismissible?: boolean;
  className?: string;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = ref.current!;
    if (!dialogStack.length) {
      bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    dialogStack.push(el);
    updateModalLayers();
    const items = focusableItems(el);
    const first = items.find((item) => item.hasAttribute('data-autofocus')) ?? items[0] ?? el;
    first.focus();
    const onKey = (e: KeyboardEvent) => {
      // A confirmation can be portalled above another dialog. Only the top layer owns keys.
      if (topDialog() !== el) return;
      // A floating layer opened from inside this dialog (the font picker) owns its own Esc and Tab.
      if (e.target instanceof Element && e.target.closest('[data-floating-layer]')) return;
      if (e.key === 'Escape') {
        // A menu opened from inside this dialog (the folder tree's "⋯" in the drawer) closes first.
        if (document.querySelector('body > .xc-popover')) return;
        e.stopPropagation();
        e.preventDefault();
        if (dismissibleRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableItems(el);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      if (e.shiftKey && (document.activeElement === firstEl || !el.contains(document.activeElement))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && (document.activeElement === lastEl || !el.contains(document.activeElement))) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const wasTop = topDialog() === el;
      const index = dialogStack.indexOf(el);
      if (index !== -1) dialogStack.splice(index, 1);
      el.inert = false;
      updateModalLayers();
      if (!dialogStack.length) document.body.style.overflow = bodyOverflow;
      const current = topDialog();
      if (wasTop && opener && document.contains(opener) && (!current || current.contains(opener))) opener.focus();
    };
  }, []);

  return createPortal(
    <div
      className="xc-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && closeOnBackdrop && dismissible) onClose();
      }}
    >
      <div ref={ref} className={`xc-dialog xc-dialog-${size}${className ? ' ' + className : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="xc-dialog-head">
          <h2 id={titleId}>{title}</h2>
          {dismissible && (
            <button type="button" className="xc-icon-btn" onClick={onClose} aria-label="ปิดหน้าต่าง">
              ✕
            </button>
          )}
        </header>
        <div className="xc-dialog-body">{children}</div>
        {footer && <footer className="xc-dialog-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'ยืนยัน',
  danger,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title={title}
      onClose={onCancel}
      size="sm"
      footer={
        <>
          <button type="button" className="xc-btn" onClick={onCancel} data-autofocus={danger || undefined}>
            ยกเลิก
          </button>
          <button type="button" className={`xc-btn ${danger ? 'xc-btn-danger' : 'xc-btn-primary'}`} onClick={onConfirm} data-autofocus={!danger || undefined}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
}
